import { Card, Flex, Heading } from '@utilitywarehouse/hearth-react';
import { MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BlockedSignupModal } from '@components/BlockedSignupModal/BlockedSignupModal';
import { DialogID, useDialog } from '@components/Dialog';
import { NavBar } from '@components/NavBar';
import { paths } from '@constants/paths.ts';
import { useEnergyContext } from '@features/Energy/Provider';
import { containsPrepaymentConstraint } from '@features/Energy/utils';
import { BundleBasketFragment } from '@generated/gql/graphql';
import { EnergyDetailsFormValues } from '@src/types/energy';

import { EnergyForm } from './EnergyForm';
import { SaveChangesModal } from './SaveChangesModal';
import { SkeletonLoader } from './SkeletonLoader';
import { getInitialValues } from './utils';

interface EnergyDetailsFormUIProps {
  formRef: RefObject<HTMLFormElement | null>;
  initialValues: Nullable<EnergyDetailsFormValues>;
  isLoading: boolean;
  nextDisabled: boolean;
  onValidated: (isValid: boolean) => void;
  onSubmit: (values: EnergyDetailsFormValues) => void;
  onNext: (event: MouseEvent) => void;
  onSaveChanges: (event: MouseEvent) => void;
  onBlockedSignupClose: () => void;
}

const EnergyDetailsFormUI = ({
  formRef,
  initialValues,
  isLoading,
  nextDisabled,
  onValidated,
  onSubmit,
  onNext,
  onSaveChanges,
  onBlockedSignupClose,
}: EnergyDetailsFormUIProps) => (
  <>
    <section className="wrapper">
      <Card
        direction="column"
        variant="subtle"
        colorScheme="neutralStrong"
        className="energyDetailsForm"
      >
        <Heading marginBottom="200" as="h2" size="lg" id="energyDetails">
          Energy details
        </Heading>
        <Flex width="100%">
          {!initialValues ? (
            <SkeletonLoader />
          ) : (
            <EnergyForm
              ref={formRef}
              onValidated={onValidated}
              onSubmit={onSubmit}
              initialValues={initialValues}
              isLoading={isLoading}
            />
          )}
        </Flex>
      </Card>
    </section>
    <NavBar
      nextLabel="Save details"
      onNext={onNext}
      nextDisabled={nextDisabled}
    />
    <SaveChangesModal handleGetQuote={onSaveChanges} />
    <BlockedSignupModal onClose={onBlockedSignupClose} />
  </>
);

const EnergyDetailsForm = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [isValid, setIsValid] = useState(false);
  const dialog = useDialog(DialogID.ENERGY_BLOCKED);
  const {
    basketRaw,
    energyDetailsErrors: errors,
    handleEnergyDetailsSubmit,
    isDetailsLoading: isLoading,
  } = useEnergyContext();

  // Basket data availability is checked on Energy provider level
  const basket = basketRaw as BundleBasketFragment;

  useEffect(() => {
    dialog.setOpen(containsPrepaymentConstraint(errors));
  }, [errors, dialog]);

  // TODO possible redirect back to /bundle, since no one should get here if
  // `energy: null` anyway, unless they are manually navigating for some reason
  if (!basket.energy) return null;

  const handleGetQuote = (event: MouseEvent) => {
    event.preventDefault();
    formRef?.current?.requestSubmit();
  };

  const isSubmitDisabled = !isValid || isLoading;

  const handleBlockedSignupClose = () => {
    dialog.setOpen(false);
    navigate(paths.root);
  };

  const initialValues = getInitialValues(basket.energy);

  return (
    <EnergyDetailsFormUI
      formRef={formRef}
      initialValues={initialValues}
      isLoading={isLoading}
      nextDisabled={isSubmitDisabled}
      onValidated={setIsValid}
      onSubmit={handleEnergyDetailsSubmit}
      onNext={handleGetQuote}
      onSaveChanges={handleGetQuote}
      onBlockedSignupClose={handleBlockedSignupClose}
    />
  );
};

export type { EnergyDetailsFormUIProps };

export { EnergyDetailsForm, EnergyDetailsFormUI };
